import React from 'react';
import {
  ClockIcon,
  UserIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  PlayIcon,
  PauseIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  CogIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline';

interface WorkflowStep {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

interface WorkflowProgressProps {
  ticketType: string;
  currentState: string;
  problemState?: string;
  changeState?: string;
}

// Define workflows for each ticket type
const WORKFLOWS: Record<string, WorkflowStep[]> = {
  INCIDENT: [
    { id: 'new', name: 'New', icon: ClockIcon, color: 'bg-blue-100 text-blue-800' },
    { id: 'in_progress', name: 'In Progress', icon: UserIcon, color: 'bg-orange-100 text-orange-800' },
    { id: 'resolved', name: 'Resolved', icon: CheckIcon, color: 'bg-green-100 text-green-800' },
    { id: 'closed', name: 'Closed', icon: XMarkIcon, color: 'bg-gray-100 text-gray-800' }
  ],
  PROBLEM: [
    { id: 'new', name: 'New', icon: ClockIcon, color: 'bg-blue-100 text-blue-800' },
    { id: 'assess_1', name: 'Assess 1', icon: ExclamationTriangleIcon, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'assess_2', name: 'Assess 2', icon: ExclamationTriangleIcon, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'root_cause_analysis', name: 'Root Cause Analysis', icon: LightBulbIcon, color: 'bg-orange-100 text-orange-800' },
    { id: 'resolved', name: 'Resolved', icon: CheckIcon, color: 'bg-green-100 text-green-800' },
    { id: 'closed', name: 'Closed', icon: XMarkIcon, color: 'bg-gray-100 text-gray-800' }
  ],
  CHANGE: [
    { id: 'new', name: 'New', icon: ClockIcon, color: 'bg-blue-100 text-blue-800' },
    { id: 'assess', name: 'Assess', icon: DocumentCheckIcon, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'authorize', name: 'Authorize', icon: CheckIcon, color: 'bg-indigo-100 text-indigo-800' },
    { id: 'scheduled', name: 'Scheduled', icon: CalendarIcon, color: 'bg-purple-100 text-purple-800' },
    { id: 'implement', name: 'Implement', icon: CogIcon, color: 'bg-orange-100 text-orange-800' },
    { id: 'review', name: 'Review', icon: DocumentCheckIcon, color: 'bg-teal-100 text-teal-800' },
    { id: 'closed', name: 'Closed', icon: XMarkIcon, color: 'bg-gray-100 text-gray-800' }
  ],
  SERVICE_REQUEST: [
    { id: 'new', name: 'New', icon: ClockIcon, color: 'bg-blue-100 text-blue-800' },
    { id: 'in_progress', name: 'In Progress', icon: DocumentCheckIcon, color: 'bg-orange-100 text-orange-800' },
    { id: 'resolved', name: 'Resolved', icon: CheckIcon, color: 'bg-green-100 text-green-800' },
    { id: 'closed', name: 'Closed', icon: CheckIcon, color: 'bg-gray-100 text-gray-800' }
  ],
  CASE: [
    { id: 'new', name: 'New', icon: ClockIcon, color: 'bg-blue-100 text-blue-800' },
    { id: 'open', name: 'Open', icon: UserIcon, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'in_progress', name: 'In Progress', icon: PlayIcon, color: 'bg-orange-100 text-orange-800' },
    { id: 'awaiting_info', name: 'Awaiting Info', icon: ChatBubbleLeftRightIcon, color: 'bg-purple-100 text-purple-800' },
    { id: 'on_hold', name: 'On Hold', icon: PauseIcon, color: 'bg-indigo-100 text-indigo-800' },
    { id: 'solution_proposed', name: 'Solution Proposed', icon: LightBulbIcon, color: 'bg-teal-100 text-teal-800' },
    { id: 'closed', name: 'Closed', icon: CheckIcon, color: 'bg-green-100 text-green-800' },
    { id: 'cancelled', name: 'Cancelled', icon: XMarkIcon, color: 'bg-red-100 text-red-800' }
  ]
};

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({
  ticketType,
  currentState,
  problemState,
  changeState
}) => {
  // Get the appropriate workflow based on ticket type
  const workflow = WORKFLOWS[ticketType] || [];

  // For problems and changes, use their specific state fields if available
  let activeState = currentState;
  if (ticketType === 'PROBLEM' && problemState) {
    activeState = problemState;
  } else if (ticketType === 'CHANGE' && changeState) {
    activeState = changeState;
  }

  // Find the current step index - handle special states for SERVICE_REQUEST and INCIDENT
  let currentStepIndex = workflow.findIndex(step => step.id === activeState);

  // For SERVICE_REQUEST, handle additional states
  if (ticketType === 'SERVICE_REQUEST') {
    // Map cancelled state to the 'closed' workflow step (last step)
    if (activeState === 'cancelled') {
      currentStepIndex = workflow.length - 1; // Map to closed step
    }
    // Map various closed states to the 'closed' workflow step
    else if (['closed', 'closed_complete', 'closed_incomplete', 'skipped', 'fulfilled'].includes(activeState)) {
      currentStepIndex = workflow.findIndex(step => step.id === 'closed');
    }
    // Map other states that might not be in the simplified workflow
    else if (activeState === 'open' || activeState === 'in_progress') {
      // These states map to 'new' in the simplified workflow
      currentStepIndex = 0;
    }
  }

  // For INCIDENT, handle cancelled state
  if (ticketType === 'INCIDENT') {
    // Map cancelled state to the 'closed' workflow step (last step)
    if (activeState === 'cancelled') {
      currentStepIndex = workflow.length - 1; // Map to closed step
    }
  }

  // Special layout for SERVICE_REQUEST tickets to match Service Request Detail Page
  if (ticketType === 'SERVICE_REQUEST') {
    const isCancelled = activeState === 'cancelled';

    // Get state label and color
    const getStateInfo = () => {
      switch (activeState) {
        case 'closed_complete':
          return { label: 'Closed Complete', color: 'text-green-600' };
        case 'closed_incomplete':
          return { label: 'Closed Incomplete', color: 'text-yellow-600' };
        case 'cancelled':
          return { label: 'Cancelled', color: 'text-red-600' };
        case 'skipped':
          return { label: 'Skipped', color: 'text-gray-600' };
        case 'fulfilled':
          return { label: 'Fulfilled', color: 'text-green-600' };
        case 'closed':
          return { label: 'Closed', color: 'text-gray-600' };
        case 'pending_approval':
          return { label: 'Pending Approval', color: 'text-purple-600' };
        case 'approved':
          return { label: 'Approved', color: 'text-green-600' };
        case 'new':
          return { label: 'New', color: 'text-blue-600' };
        case 'open':
          return { label: 'Open', color: 'text-yellow-600' };
        case 'in_progress':
          return { label: 'In Progress', color: 'text-orange-600' };
        case 'resolved':
          return { label: 'Resolved', color: 'text-green-600' };
        default:
          return { label: activeState.replace('_', ' '), color: 'text-gray-600' };
      }
    };

    const stateInfo = getStateInfo();

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Workflow Progress</h3>

        <nav aria-label="Progress">
          <ol className="flex items-center">
            {workflow.map((step, stepIdx) => {
              const Icon = step.icon;
              const isComplete = stepIdx < currentStepIndex;
              const isCurrent = stepIdx === currentStepIndex;
              const isLastStep = stepIdx === workflow.length - 1;
              const showCancelledIcon = isLastStep && isCurrent && isCancelled;

              return (
                <li key={step.id} className={stepIdx !== workflow.length - 1 ? 'flex-1' : ''}>
                  <div className="flex items-center">
                    <div className="relative flex h-10 w-10 items-center justify-center rounded-full">
                      {/* Step indicator */}
                      <div className={`
                        h-10 w-10 rounded-full flex items-center justify-center border-2
                        ${showCancelledIcon
                          ? 'bg-red-100 border-transparent text-red-600'
                          : isComplete
                            ? (isCancelled ? 'bg-white border-gray-300' : 'bg-indigo-600 border-transparent')
                            : isCurrent
                              ? 'bg-indigo-600 border-transparent'
                              : 'bg-white border-gray-300'}
                      `}>
                        {showCancelledIcon ? (
                          <XMarkIcon className="h-6 w-6" />
                        ) : isComplete ? (
                          isCancelled ? (
                            <Icon className="h-6 w-6 text-gray-400" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-white" />
                          )
                        ) : isCurrent ? (
                          <Icon className="h-6 w-6 text-white" />
                        ) : (
                          <Icon className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Connector line */}
                    {stepIdx !== workflow.length - 1 && (
                      <div className={`ml-4 flex-1 h-0.5 ${
                        isComplete ? (isCancelled ? 'bg-gray-300' : 'bg-indigo-600') : 'bg-gray-300'
                      }`} />
                    )}
                  </div>

                  {/* Step label below icon */}
                  <div className="mt-2">
                    <span className={`text-sm font-medium ${
                      isComplete || isCurrent ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {showCancelledIcon ? 'Cancelled' : step.name}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Status information box */}
        {activeState === 'pending_approval' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <DocumentCheckIcon className="inline-block h-4 w-4 mr-1" />
              This request is pending approval
            </p>
          </div>
        )}

        {activeState === 'approved' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">
              <CheckIcon className="inline-block h-4 w-4 mr-1" />
              This request has been approved
            </p>
          </div>
        )}

        {(activeState === 'closed' || activeState === 'closed_complete' ||
          activeState === 'closed_incomplete' || activeState === 'cancelled' ||
          activeState === 'skipped' || activeState === 'fulfilled') && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm text-gray-800">
              <CheckIcon className="inline-block h-4 w-4 mr-1" />
              This request has been {stateInfo.label.toLowerCase()}
            </p>
          </div>
        )}

        {/* Current state and progress section */}
        <div className="mt-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Current State</p>
            <div className="mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateInfo.color} ${
                activeState === 'pending_approval' ? 'bg-purple-100' :
                activeState === 'approved' ? 'bg-green-100' :
                activeState === 'new' ? 'bg-blue-100' :
                activeState.includes('closed') || activeState === 'fulfilled' ? 'bg-gray-100' :
                activeState === 'cancelled' ? 'bg-red-100' :
                'bg-gray-100'
              }`}>
                {stateInfo.label}
              </span>
            </div>
          </div>

          {currentStepIndex >= 0 && workflow.length > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-500">Progress</p>
              <p className="text-lg font-semibold text-gray-900">
                {Math.round(((currentStepIndex + 1) / workflow.length) * 100)}%
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Special layout for INCIDENT tickets to match Incident Detail Page
  if (ticketType === 'INCIDENT') {
    const isCancelled = activeState === 'cancelled';

    return (
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Incident Workflow Progress</h3>
          <p className="text-sm text-gray-500 mt-1">Track the journey of this incident through its lifecycle</p>
        </div>
        <div className="px-6 pb-6">
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {workflow.map((step, stepIdx) => {
                const Icon = step.icon;
                const isComplete = stepIdx < currentStepIndex;
                const isCurrent = stepIdx === currentStepIndex;
                const isLastStep = stepIdx === workflow.length - 1;
                const showCancelledIcon = isLastStep && isCurrent && isCancelled;

                return (
                  <li key={step.id} className={stepIdx !== workflow.length - 1 ? 'flex-1' : ''}>
                    <div className="flex items-center">
                      <div className={`relative flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                        showCancelledIcon
                          ? 'bg-red-100 border-transparent text-red-600'
                          : isComplete
                            ? (isCancelled ? 'bg-white border-gray-300' : 'bg-indigo-600 border-transparent')
                            : isCurrent
                              ? 'bg-indigo-600 border-transparent'
                              : 'bg-white border-gray-300'
                      }`}>
                        {showCancelledIcon ? (
                          <XMarkIcon className="h-6 w-6" />
                        ) : isComplete ? (
                          isCancelled ? (
                            <Icon className="h-6 w-6 text-gray-400" />
                          ) : (
                            <CheckIcon className="h-6 w-6 text-white" />
                          )
                        ) : isCurrent ? (
                          <Icon className="h-6 w-6 text-white" />
                        ) : (
                          <Icon className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      {stepIdx !== workflow.length - 1 && (
                        <div className={`ml-4 flex-1 h-0.5 ${
                          isComplete ? (isCancelled ? 'bg-gray-300' : 'bg-indigo-600') : 'bg-gray-300'
                        }`} />
                      )}
                    </div>
                    <div className="mt-2">
                      <span className={`text-sm font-medium ${
                        isComplete || isCurrent ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {showCancelledIcon ? 'Cancelled' : step.name}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Status Bar */}
          <div className="mt-6 px-6 py-3 bg-gray-50 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Current Status:</span>
                <span className="text-sm font-semibold text-gray-900 capitalize">
                  {activeState.replace('_', ' ')}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Progress</p>
                <p className="text-lg font-semibold text-gray-900">
                  {Math.round(((currentStepIndex + 1) / workflow.length) * 100)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default layout for other ticket types
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Workflow Progress</h3>

      {workflow.length === 0 ? (
        <p className="text-gray-500">No workflow defined for this ticket type</p>
      ) : (
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {workflow.map((step, stepIdx) => {
              const Icon = step.icon;
              const isCompleted = stepIdx < currentStepIndex;
              const isCurrent = stepIdx === currentStepIndex;
              const isUpcoming = stepIdx > currentStepIndex;

              return (
                <li key={step.id} className={stepIdx !== workflow.length - 1 ? 'flex-1' : ''}>
                  <div className={`flex items-center ${stepIdx !== workflow.length - 1 ? 'w-full' : ''}`}>
                    <span className={`
                      relative flex items-center justify-center w-10 h-10 rounded-full border-2
                      ${isCompleted ? 'bg-indigo-600 border-indigo-600' : ''}
                      ${isCurrent ? 'bg-white border-indigo-600' : ''}
                      ${isUpcoming ? 'bg-white border-gray-300' : ''}
                    `}>
                      <Icon className={`
                        w-5 h-5
                        ${isCompleted ? 'text-white' : ''}
                        ${isCurrent ? 'text-indigo-600' : ''}
                        ${isUpcoming ? 'text-gray-400' : ''}
                      `} />
                      {isCurrent && (
                        <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-indigo-600 rounded-full animate-pulse" />
                      )}
                    </span>

                    {stepIdx !== workflow.length - 1 && (
                      <div className={`
                        flex-1 ml-3 h-0.5
                        ${isCompleted ? 'bg-indigo-600' : 'bg-gray-300'}
                      `} />
                    )}
                  </div>

                  <p className={`
                    mt-2 text-xs font-medium
                    ${isCompleted ? 'text-indigo-600' : ''}
                    ${isCurrent ? 'text-indigo-600 font-semibold' : ''}
                    ${isUpcoming ? 'text-gray-500' : ''}
                  `}>
                    {step.name}
                  </p>
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {/* Current state badge */}
      <div className="mt-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Current State</p>
          <div className="mt-1">
            {(() => {
              const currentStep = workflow.find(step => step.id === activeState);
              if (currentStep) {
                return (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${currentStep.color}`}>
                    {currentStep.name}
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  {activeState}
                </span>
              );
            })()}
          </div>
        </div>

        {currentStepIndex >= 0 && workflow.length > 0 && (
          <div className="text-right">
            <p className="text-sm text-gray-500">Progress</p>
            <p className="text-lg font-semibold text-gray-900">
              {Math.round(((currentStepIndex + 1) / workflow.length) * 100)}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowProgress;